import { useState } from 'react';
import { ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import api from '../../services/api';

interface ZandiShowcaseButtonProps {
  propertyId: string;
  address: string;
  analysisMode: 'off-market' | 'on-market';
  purchasePrice?: number;  // On-market listing price; off-market omits (backend uses market_value)
}

export function ZandiShowcaseButton({ propertyId, address: _address, analysisMode, purchasePrice }: ZandiShowcaseButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        mode: analysisMode,
        hold_period_years: 10,
      };
      if (purchasePrice) {
        payload.purchase_price = purchasePrice;
      }
      const response = await api.post(
        `/v2/properties/${propertyId}/zandi-showcase-v2`,
        payload,
        {
          responseType: 'blob',
          timeout: 120000,
        }
      );

      // Open HTML in new browser tab via blob URL
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a short delay to let the tab load
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      console.error('Zandi showcase generation failed:', err);
      if (axiosErr?.response?.status === 504) {
        setError('Generation timed out. Try again.');
      } else if (axiosErr?.response?.status === 503) {
        setError('Service unavailable. Try again.');
      } else {
        setError('Generation failed.');
      }
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleOpen}
        disabled={loading}
        title="Open Zandi Data Showcase"
        className="flex items-center gap-2 px-3 py-2 bg-charcoal border border-slate-dark text-stone-700 hover:text-gold hover:border-gold/50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gold" />
        ) : (
          <ExternalLink className="w-4 h-4" />
        )}
        <span>{loading ? 'Generating...' : 'Zandi Showcase'}</span>
      </button>
      {error && (
        <div className="absolute top-full mt-1 right-0 flex items-center gap-1 px-2 py-1 bg-crimson/20 border border-crimson/50 rounded text-sm text-crimson whitespace-nowrap z-10">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}
